import { 
    useState, 
    useEffect, 
} from 'react';
import { 
    useSelector, 
    useDispatch,
} from 'react-redux';
import { 
    useNavigate,
    useParams,
} from 'react-router-dom';
import { 
    Container,
    Box,
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { withPather } from 'react-pather';
import ConversionHead from './ConversionHead';
import Details from './Details';
import LinkDisplay from './LinkDisplay';
import ConversionDescription from './ConversionDescription';
import { RedirectsDisplay } from '../general';
import Loader from '../../Loader';
import { PasswordField } from '../../general';
import { useEffectExceptOnMount } from '../../../hooks';
import { setConversion } from '../../../slices/conversionSlice';

const ConversionView = ({ pather }) => {

    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { code } = useParams();

    // General
    const conversion = useSelector(state => state.conversion.value);
    
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [description, setDescription] = useState('');

    // Passcode
    const [passcode, setPasscode] = useState('');
    const [passcodeLoading, setPasscodeLoading] = useState(false);
    const [showPasscode, setShowPasscode] = useState(false);
    const handleClickShowPasscode = () => setShowPasscode(show => !show);

    // Storing Advanced settings
    const [advanced, setAdvanced] = useState({
        maxRedirects: conversion.maxRedirects,
        revealable: conversion.revealable,
        isDead: conversion.isDead,
        passcode: conversion.passcode,
    });

    // Safe update
    const setAdvancedSafe = (updStateObj) => {
        setAdvanced((prevState) => ({
            ...prevState,
            ...updStateObj,
        }));    
    }
    //

    useEffect(() => {
        setAdvancedSafe({
            maxRedirects: conversion.maxRedirects,
            revealable: conversion.revealable,
            isDead: conversion.isDead,
            passcode: conversion.passcode,
        });
        setDescription(conversion.description);
    }, [conversion])

    const fetchPasscode = (_setPasscode, _setPasscodeLoading) => {
        _setPasscodeLoading(true);
        axios.get(
            pather.reverse(pather.back.Conversion.passcode, { code }),   
        )
        .then(response => {
            const passcode = response?.data?.passcode;
            if (passcode) _setPasscode(passcode);
        })
        .catch(response => {
            const statusCode = response?.status;
            const data = response?.response?.data;
            const ignore = data?.ignore;
            const status = data?.status;
            if (!ignore && statusCode !== 404) {
                console.log(response);
                if(status) enqueueSnackbar(status, { variant: 'error' });
                else enqueueSnackbar('Something went wrong during passcode retrieval', { variant: 'error' });
            }
        })
        .finally(() => {
            _setPasscodeLoading(false);    
        });
    }

    useEffectExceptOnMount(() => {
        if (!showPasscode) {
            setPasscode('');
            return;
        }
        fetchPasscode(setPasscode, setPasscodeLoading);
    }, [showPasscode]);

    useEffect(() => {
        // \/ TODO: Stale data may exist in Redux (Redux Conversion Data are not up to date after Conversion update)
        // if(conversion.code && conversion.code === code) return; 
        setLoading(true);
        axios.get(
            pather.reverse(pather.back.Conversion.code, { code }),   
        )
        .then(response => {
            const conversion = response?.data?.conversion;
            if (conversion?.code) dispatch(setConversion(conversion));
        })
        .catch(error => {
            console.log(error);
            const status = error?.data?.status;
            if(status) setError(status);
            else {
                if (error?.response?.status === 404) setError('Resource is not found!');
                else setError('Something went wrong!');
            }
        })
        .finally(() => {
            setLoading(false);    
        });
    }, [])

    const toggleFavourite = () => {
        setLoading(true);
        axios.post(
            pather.reverse(pather.back.Conversion.favourite, { code: conversion.code })
        )
        .then(response => {
            const conversion = response?.data?.conversion;
            if (conversion?.code) dispatch(setConversion(conversion));
        })
        .catch(err => {
            console.log(err);
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const successDeactivation = () => {
        setLoading(true);
        axios.put(
            pather.reverse(pather.back.Conversion.deactivateConv, { code }),
        )
        .then(response => {
            const conversion = response?.data?.conversion;
            if (conversion?.code) dispatch(setConversion(conversion));
        })
        .catch(err => {
            console.log(err);
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const isInputValid = () => {
        if ((advanced.maxRedirects != null && advanced.maxRedirects <= 0) || (advanced.maxRedirects != null && isNaN(parseInt(advanced.maxRedirects)))) {
            enqueueSnackbar('Max redirects number must be bigger that 0!', { variant: 'error' });
            return false;
        }
        if (advanced.passcode !== conversion.passcode){
            if (advanced.passcode && advanced.passcode.length < 5){
                enqueueSnackbar('Passcode must contain at least 5 characters!', { variant: 'error' });
                return false;
            }
        }
        return true;
    }

    const edit = () => {
        setLoading(true);
        if (!isInputValid()) {
            setLoading(false);
            return;
        }
        axios.put(
            pather.reverse(pather.back.Conversion.code, { code }), 
            {
                source: conversion.source,
                description: description ? description.trim() : description,
                ...advanced,
            }
        )
        .then(response => {
            const conversion = response?.data?.conversion;
            if (conversion?.code) {
                dispatch(setConversion(conversion));
                if (showPasscode) setShowPasscode(false);
            }
        })
        .catch(err => {
            const status = err?.response?.data?.status;
            if (!!status) {
                enqueueSnackbar(status, { variant: 'error' });
                setAdvancedSafe({ // TODO: how good is it?
                    maxRedirects: conversion.maxRedirects,
                })
            }
            console.log(error);
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const successForget = () => {
        setLoading(true);
        axios.put(pather.reverse(pather.back.Conversion.forget, { code }))
        .then(response => {
            const data = response?.data;
            const conversion = data.conversion;
            if (conversion) dispatch(setConversion(conversion));
        })
        .catch(err => {
            const data = err?.response?.data;
            if (data?.status) {
                enqueueSnackbar(data.status, { variant: 'error' });
                setAdvancedSafe({
                    maxRedirects: conversion.maxRedirects,
                })
            }
            console.log(error);
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const handleDeleteForever = code => {
        setLoading(true);
        axios.delete(
            pather.reverse(pather.back.Conversion.delete, { code })
        ).then(() => {
            enqueueSnackbar('Successfully deleted', { variant: 'success' });
            navigate(-1);  // Navigate to previous page
        })
        .catch(error => {
            console.log(error);
            const status = error?.response?.data?.status;
            status && enqueueSnackbar(status, { variant: 'error' });
        })
        .finally(() => {
            setLoading(false);
        });
    }

    return (
        <Container sx={{height: '100%'}}>
            {
                loading || (!conversion?.code && !error) ? 
                <Loader boxMinHeight='550px'/> :
                <>
                    {
                        error ? 
                        <Box sx={{display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center', minHeight: '550px'}}>
                            <h1>{error} &#128546;</h1>
                        </Box> :
                        <Box>
                            <Box sx={{my: 2, fontSize: {md: 16, xs: 12}, width: '100%', display: 'flex', flexDirection: 'column'}}>     
                                <ConversionHead 
                                    conversion={conversion} 
                                    toggleFavourite={toggleFavourite}
                                    successDeactivation={successDeactivation}
                                    edit={edit}
                                    fetchPasscode={fetchPasscode}
                                    advanced={advanced}
                                    setAdvancedSafe={setAdvancedSafe}
                                    successForget={successForget}
                                    handleDeleteForever={handleDeleteForever}
                                />
                                <Box sx={{width: {xs: '86%', sm: '93%', md: '97%'}}}>
                                    <LinkDisplay 
                                        link={`${window.location.origin}${pather.reverse(pather.front.main, { code: conversion.code })}`}
                                        label='Shortened link: '
                                    />
                                </Box>
                                <Box sx={{width: {xs: '86%', sm: '93%', md: '97%'}}}>
                                    <LinkDisplay 
                                        link={conversion.source}
                                        label='Source link: '
                                    />
                                </Box>
                                {
                                    !!conversion.passcodeLength &&
                                    <Passcode 
                                        showPasscode={showPasscode}
                                        passcode={passcode} 
                                        conversion={conversion} 
                                        handleClickShowPasscode={handleClickShowPasscode} 
                                        passcodeLoading={passcodeLoading} 
                                    />
                                }
                                <Details 
                                    conversion={conversion} 
                                    setConversion={(newConv) => dispatch(setConversion(newConv))}
                                />
                                <ConversionDescription
                                    edit={edit}
                                    description={description}
                                    setDescription={setDescription}
                                    conversion={conversion}
                                />
                                <RedirectsDisplay code={code} />
                            </Box>
                        </Box>
                    }
                </>
            }
        </Container>
    );
}

const Passcode = props => {
    const { 
        showPasscode, 
        passcode, 
        conversion, 
        handleClickShowPasscode, 
        passcodeLoading, 
    } = props;
    return (
        <Box sx={{width: {xs: '93%', sm: '95%', md: '99%'}, mb: 3}}>
            <Box>
                <h3>Passcode: </h3>
            </Box>
            <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                <PasswordField
                    value={showPasscode ? passcode : '*'.repeat(conversion.passcodeLength)}
                    show={showPasscode}
                    handleShow={handleClickShowPasscode}
                    style={{
                        width: {xs: '100%', sm: '40vw', md: '30vw'},
                        '& .MuiInputBase-root': {
                            borderRadius: '12px',
                        },
                    }}
                    loading={passcodeLoading}
                />
            </Box>
        </Box>
    );
}

export default withPather()(ConversionView);